'use client';

/**
 * useTemplateHydration Hook
 *
 * Manages the initialization and hydration of the editor with template content.
 * Handles both clearing the editor when criteria is removed and populating
 * it when new criteria is set.
 */

import { useEffect, useRef, RefObject, MutableRefObject } from 'react';
import { fromReduxInput, ComponentData } from '../utils/criteriaTransformer';

interface DynamicCriteria {
  criteria_template: string;
  criteria_inputs?: Record<string, any>;
}

interface UseTemplateHydrationParams {
  editorRef: RefObject<HTMLDivElement>;
  lastContentRef: MutableRefObject<string>;
  dynamicCriteria: DynamicCriteria | null;
  renderComponentByType: (
    pillElement: HTMLElement,
    componentData: ComponentData,
    inputData: any
  ) => void;
}

/**
 * Hook that handles template hydration for the DynamicCriteriaGenerator.
 *
 * Responsibilities:
 * - Clears the editor when dynamicCriteria becomes null
 * - Hydrates the editor with template content and component pills
 * - Renders component portals for each {{ componentId }} placeholder
 */
export function useTemplateHydration({
  editorRef,
  lastContentRef,
  dynamicCriteria,
  renderComponentByType,
}: UseTemplateHydrationParams): void {
  // Track whether we've initialized the current template
  const hasInitializedRef = useRef(false);

  // Clear editor when criteria is removed
  useEffect(() => {
    if (!dynamicCriteria) {
      hasInitializedRef.current = false;
      if (editorRef.current) {
        editorRef.current.innerHTML = '';
        lastContentRef.current = '';
      }
    }
  }, [dynamicCriteria, editorRef, lastContentRef]);

  // Hydrate editor with template content
  useEffect(() => {
    // Skip if no template, no editor, editor has content, or already initialized
    if (
      !dynamicCriteria?.criteria_template ||
      !editorRef.current ||
      editorRef.current.textContent?.trim() ||
      hasInitializedRef.current
    ) {
      return;
    }

    hasInitializedRef.current = true;
    const { criteria_template, criteria_inputs } = dynamicCriteria;
    const hasPlaceholders = criteria_template.includes('{{');
    const hasInputs = criteria_inputs && Object.keys(criteria_inputs).length > 0;

    if (hasPlaceholders && hasInputs) {
      // Template has component placeholders - replace with pill elements
      let htmlContent = criteria_template;
      const newComponents = new Map<string, ComponentData>();

      Object.entries(criteria_inputs).forEach(([componentId, input]) => {
        const componentData = fromReduxInput(componentId, input);
        newComponents.set(componentId, componentData);

        const pillHtml = `<span contenteditable="false" data-component-id="${componentId}" data-component-type="${input.type}" class="component-pill" style="display: inline; vertical-align: baseline;"></span>`;
        htmlContent = htmlContent.replace(`{{ ${componentId} }}`, pillHtml);
      });

      editorRef.current.innerHTML = htmlContent;
      lastContentRef.current = htmlContent;

      // Render component portals after DOM is updated
      setTimeout(() => {
        newComponents.forEach((componentData, componentId) => {
          const pillElement = editorRef.current?.querySelector(
            `[data-component-id="${componentId}"]`
          );
          if (pillElement && criteria_inputs) {
            renderComponentByType(
              pillElement as HTMLElement,
              componentData,
              criteria_inputs[componentId]
            );
          }
        });
      }, 0);
    } else {
      // Plain text template - just set the content
      editorRef.current.innerHTML = criteria_template;
      lastContentRef.current = criteria_template;
    }
  }, [dynamicCriteria, editorRef, lastContentRef, renderComponentByType]);
}
